import React, { useMemo } from "react";
import PropTypes from 'prop-types';
import {
  buildDayArray,
  getVisibleMonthRange,
  dayFractionFromStart,
  getDayKey,
  isSunday,
  ITEM_OFFSET_PX,
  computeOffsetsForWidth
} from "./timeLineUtils";

export default function Timeline({
  options,
  items = [],
  groups = [],
  itemClickHandler,
  backgroundClickHandler,
  selectedDate
}) {
  const today = new Date();
  const locale = options?.locale || 'en';

  const { startDate, days } = useMemo(
    () => getVisibleMonthRange(items, selectedDate),
    [items, selectedDate]
  );

  const dayArray = useMemo(() => buildDayArray(startDate, days), [startDate, days]);

  // Responsive offsets based on window width. Update on resize.
  const [widthPx, setWidthPx] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') {return undefined;}
    const onResize = () => setWidthPx(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { startOffsetPct, endOffsetPct } = React.useMemo(
    () => computeOffsetsForWidth(widthPx),
    [widthPx]
  );

  const isToday = (d) => d.toDateString() === today.toDateString();

  const isMonthStart = (day, index) => index === 0 || day.getMonth() !== dayArray[index - 1].getMonth();

  return (
    <div className="timeline">
      {/* HEADER */}
      <div className="timeline-header">
        <div className="timeline-group-col" />
        <div className="timeline-days-header">
          {dayArray.map((day, i) => (
            <React.Fragment key={i}>
              <div className="timeline-month-header">
                {isMonthStart(day, i) && (
                  <div className="month">
                    {options.translate?.(
                      day.toLocaleString(locale, { month: "long" }).toLowerCase()
                    ) || day.toLocaleString(locale, { month: "long" })} {day.getFullYear()}
                  </div>
                )}
              </div>
              <div
                className={`timeline-day-header
                  ${isToday(day) ? "today" : ""}
                  ${isSunday(day) ? "week-separator" : ""}
                `}
              >
                <div className="day-number">{day.getDate()}</div>
                <div className="day-name">
                  {options.translate?.(getDayKey(day)) || getDayKey(day)}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="timeline-body">
        {groups.map(group => (
          <div className={`timeline-row ${group.className || ''}`} key={group.propertyId}>
            <div className="timeline-group-col">{group.content}</div>
            <div className="timeline-grid">
              {dayArray.map((day, i) => (
                <div
                  key={i}
                  className={`timeline-cell
                    ${isToday(day) ? "today" : ""}
                    ${isSunday(day) ? "week-separator" : ""}
                  `}
                  onClick={(e) => backgroundClickHandler?.({
                    event: e.nativeEvent,
                    propertyId: group.propertyId,
                    item: null,
                    time: day
                  })}
                />
              ))}

              {items.filter(item => item.propertyId === group.propertyId).map(item => {
                const start = new Date(item.start);
                const end = new Date(item.end);

                const itemStartPos = dayFractionFromStart(start, startDate);
                const itemEndPos = dayFractionFromStart(end, startDate);

                // Skip items that do not overlap the visible range
                if (itemEndPos <= 0 || itemStartPos >= days) {return null;}

                const leftPercent = (itemStartPos / days) * 100 - startOffsetPct;
                const widthPercent = ((itemEndPos - itemStartPos) / days) * 100 - endOffsetPct;

                return (
                  <div
                    key={item.id}
                    className={`timeline-item ${item.className || ""}`}
                    style={{
                      left: `calc(${leftPercent}% + ${ITEM_OFFSET_PX}px)`,
                      width: `calc(${widthPercent}% + ${ITEM_OFFSET_PX}px)`
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      itemClickHandler?.(item
                      //   {
                      //   event: e.nativeEvent,
                      //   group: item.group,
                      //   item: item.id
                      // }
                    );
                    }}
                  >
                    {item.content}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

Timeline.propTypes = {
  options: PropTypes.object,
  items: PropTypes.array,
  groups: PropTypes.array,
  itemClickHandler: PropTypes.func,
  backgroundClickHandler: PropTypes.func,
  selectedDate: PropTypes.object
};
